import moment from "moment";
import { useState } from "react";
import { useParams } from "react-router";
import MainLayout from "@/components/layouts";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { TrashIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { getInitials } from "@/lib/utils";
import { useVendorDetail } from "./vendor-detail.hooks";
import { useAdministersActiveOrganization } from "@/hooks/useAdministersActiveOrganization";
import { LinkableProperty, ServicedProperty } from "@/repositories/vendors.repository";
import type { DocumentRecord } from "@shyne/shared/api";
import { DocumentOpenLink } from "@/components/shared/documents/document-open-link";
import { shownDay } from "@/lib/documents";
import SimpleTable, { ColumnDef } from "@/components/shared/simple-table";
import DocumentUploadModal from "@/components/shared/upload-document/document-upload-modal.tsx";
import ParentServicesSection from "./components/ParentServicesSection";

export default function VendorDetailPage() {
  const { vendorId } = useParams<{ vendorId: string }>();
  const {
    detail,
    loading,
    error,
    uploadDocument,
    deleteDocument,
    deletingDoc,
    documents,
    docTotal,
    docPage,
    docPageSize,
    setDocPage,
    setDocPageSize,
    properties,
    propLoading,
    propPage,
    propPageSize,
    propTotal,
    setPropPage,
    setPropPageSize,
    addPropertyToVendor,
    removeProperty,
    removingProperty,
    availableProperties,
    loadAvailableProperties,
    services,
  } = useVendorDetail(vendorId);
  const administersCustomer = useAdministersActiveOrganization();
  const [docModalOpen, setDocModalOpen] = useState(false);
  const [removingDocId, setRemovingDocId] = useState<string | null>(null);
  const [propModalOpen, setPropModalOpen] = useState(false);
  const [removingPropId, setRemovingPropId] = useState<string | null>(null);
  const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
  const [propertySearch, setPropertySearch] = useState("");
  const [loadingProperties, setLoadingProperties] = useState(false);

  const handleOpenPropertyModal = async () => {
    setPropModalOpen(true);
    setLoadingProperties(true);
    try {
      await loadAvailableProperties();
    } finally {
      setLoadingProperties(false);
    }
  };

  const handlePropertySearch = async (search: string) => {
    setPropertySearch(search);
    setLoadingProperties(true);
    try {
      await loadAvailableProperties(search);
    } finally {
      setLoadingProperties(false);
    }
  };

  const handlePropertySelect = (propertyId: string, checked: boolean) => {
    if (checked) {
      setSelectedProperties((prev) => [...prev, propertyId]);
    } else {
      setSelectedProperties((prev) => prev.filter((id) => id !== propertyId));
    }
  };

  const handleAddSelectedProperties = async () => {
    if (selectedProperties.length === 0) return;

    setLoadingProperties(true);
    try {
      for (const propertyId of selectedProperties) {
        await addPropertyToVendor(propertyId);
      }
      setSelectedProperties([]);
      setPropModalOpen(false);
      setPropertySearch("");
    } finally {
      setLoadingProperties(false);
    }
  };

  const handleClosePropertyModal = () => {
    setPropModalOpen(false);
    setSelectedProperties([]);
    setPropertySearch("");
  };

  const breadcrumb = [
    { name: "Vendors", href: "/vendors" },
    { name: detail?.vendor.name ?? "Loading…", href: "#" },
  ];

  if (!loading && !detail && error) {
    return (
      <MainLayout breadcrumb={breadcrumb}>
        <p className="text-center text-muted-foreground py-6">{error}</p>
      </MainLayout>
    );
  }

  if (loading || !detail) {
    return (
      <MainLayout breadcrumb={breadcrumb}>
        <div className="space-y-6">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </MainLayout>
    );
  }

  const { vendor } = detail;

  const removeColumn: ColumnDef<ServicedProperty> = {
    header: "Actions",
    accessor: (p) => (
      <Dialog open={removingPropId === p.id} onOpenChange={(o) => setRemovingPropId(o ? p.id : null)}>
        <DialogTrigger asChild>
          <Button variant="destructive" size="icon" aria-label={`Remove ${p.name}`}>
            <TrashIcon className="w-4 h-4" />
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Property Access?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to remove access to "{p.name}"? This will prevent the vendor from
            managing this property.
          </p>
          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={() => setRemovingPropId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                await removeProperty(p.id);
                setRemovingPropId(null);
              }}
              disabled={removingProperty}
            >
              Remove Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    ),
    className: "text-right whitespace-nowrap",
  };

  const propColumns: ColumnDef<ServicedProperty>[] = [
    {
      header: "Property",
      accessor: (p) => (
        <div className="flex flex-col">
          <span className="font-medium">{p.name}</span>
          {p.address && <span className="text-sm text-muted-foreground">{p.address}</span>}
        </div>
      ),
    },
    {
      header: "Type",
      accessor: (p) => p.types.join(", ") || "—",
    },
    {
      header: "Added",
      accessor: (p) => moment(p.linkedAt).format("MM/DD/YYYY"),
    },
  ];

  const docColumns: ColumnDef<DocumentRecord>[] = [
    {
      header: "Document",
      accessor: (d) => <DocumentOpenLink document={d} />,
    },
    {
      header: "Uploaded",
      accessor: (d) => moment(d.uploadedAt).format("MM/DD/YYYY"),
    },
    {
      header: "Expires",
      accessor: (d) => shownDay(d.expiresOn),
    },
  ];

  const docDeleteColumn: ColumnDef<DocumentRecord> = {
    header: "Actions",
    accessor: (d) => (
      <Dialog open={removingDocId === d.id} onOpenChange={(o) => setRemovingDocId(o ? d.id : null)}>
        <DialogTrigger asChild>
          <Button variant="destructive" size="icon" aria-label={`Delete ${d.filename}`}>
            <TrashIcon className="w-4 h-4" />
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Document?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete “{d.filename}”? This cannot be undone.
          </p>
          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={() => setRemovingDocId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                await deleteDocument(d.id);
                setRemovingDocId(null);
              }}
              disabled={deletingDoc}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    ),
    className: "text-right whitespace-nowrap",
  };

  return (
    <MainLayout breadcrumb={breadcrumb}>
      <div className="flex flex-col gap-8">
        <Card>
          <CardContent className="flex items-center gap-4">
            <Avatar className="w-16 h-16">
              <AvatarImage src={vendor.image ?? undefined} />
              <AvatarFallback>{getInitials(vendor.name)}</AvatarFallback>
            </Avatar>
            <div>
              <h2 className="text-2xl font-semibold">{vendor.name}</h2>
              <p className="text-sm text-muted-foreground">{vendor.email}</p>
              <p className="text-sm text-muted-foreground">{vendor.phone}</p>
            </div>
          </CardContent>
        </Card>

        <ParentServicesSection services={services} />

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Documents</CardTitle>
              {administersCustomer && (
                <Button size="sm" onClick={() => setDocModalOpen(true)}>
                  Upload Document
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {documents.length > 0 ? (
              <SimpleTable<DocumentRecord>
                columns={administersCustomer ? [...docColumns, docDeleteColumn] : docColumns}
                data={documents}
                totalCount={docTotal}
                page={docPage}
                pageSize={docPageSize}
                setPage={setDocPage}
                setPageSize={setDocPageSize}
                sortKey={undefined}
                sortOrder="asc"
                onSortChange={() => {}}
                filters={[]}
                onReset={() => {}}
                onApply={() => {}}
              />
            ) : (
              <p className="text-center text-muted-foreground py-6">No documents.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Managed Properties</CardTitle>
              {administersCustomer && (
                <Button size="sm" onClick={handleOpenPropertyModal}>
                  Add Property
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {properties.length > 0 ? (
              <SimpleTable<ServicedProperty>
                columns={administersCustomer ? [...propColumns, removeColumn] : propColumns}
                data={properties}
                totalCount={propTotal}
                page={propPage}
                pageSize={propPageSize}
                setPage={setPropPage}
                setPageSize={setPropPageSize}
                sortKey={undefined}
                sortOrder="asc"
                onSortChange={() => {}}
                filters={[]}
                onReset={() => {}}
                onApply={() => {}}
              />
            ) : (
              <p className="text-center text-muted-foreground py-6">No properties assigned.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <DocumentUploadModal
        open={docModalOpen}
        onOpenChange={setDocModalOpen}
        onUpload={async (file, expiresAt) => {
          if (await uploadDocument(file, expiresAt)) setDocModalOpen(false);
        }}
      />

      <Dialog open={propModalOpen} onOpenChange={handleClosePropertyModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Property Access</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              Select properties to give this vendor access to manage.
            </p>

            <div className="mb-4">
              <Label htmlFor="property-search">Search Properties</Label>
              <Input
                id="property-search"
                placeholder="Search by property name or address..."
                value={propertySearch}
                onChange={(e) => handlePropertySearch(e.target.value)}
                className="mt-1"
              />
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto border rounded-md p-2">
              {loadingProperties ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-sm text-muted-foreground">Loading properties...</div>
                </div>
              ) : availableProperties.length > 0 ? (
                availableProperties.map((property: LinkableProperty) => (
                  <div key={property.id} className="flex items-center space-x-3 p-2 hover:bg-muted/50 rounded">
                    <Checkbox
                      id={`property-${property.id}`}
                      checked={selectedProperties.includes(property.id)}
                      onCheckedChange={(checked) => handlePropertySelect(property.id, checked as boolean)}
                    />
                    <Label htmlFor={`property-${property.id}`} className="flex-1 cursor-pointer">
                      <div className="flex flex-col">
                        <span className="font-medium">{property.name}</span>
                        {property.address && <span className="text-sm text-muted-foreground">{property.address}</span>}
                        {property.types.length > 0 && (
                          <span className="text-xs text-muted-foreground">Type: {property.types.join(", ")}</span>
                        )}
                      </div>
                    </Label>
                  </div>
                ))
              ) : (
                <div className="text-center text-muted-foreground py-8">
                  {propertySearch ? "No properties found matching your search." : "No available properties to assign."}
                </div>
              )}
            </div>

            {selectedProperties.length > 0 && (
              <div className="mt-4 p-3 bg-muted rounded-md">
                <p className="text-sm font-medium">
                  {selectedProperties.length} propert{selectedProperties.length === 1 ? "y" : "ies"} selected
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleClosePropertyModal}>
              Cancel
            </Button>
            <Button
              onClick={handleAddSelectedProperties}
              disabled={selectedProperties.length === 0 || loadingProperties}
            >
              Add Selected Properties ({selectedProperties.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
